/**
 * Euristica LEGGERA per proporre il tipo di grafico. Funzione PURA:
 * nessun import React, nessuna UI. Testabile da sola.
 *
 * Regole (in ordine, dal design):
 *   0 righe                         -> 'table'
 *   1 riga con 1 sola cifra          -> 'number'
 *   1 colonna data/mese + 1 numerica -> 'line'
 *   1 colonna testo   + 1 numerica   -> 'bar'
 *   altrimenti                       -> 'table'
 *
 * Il backend NON decide il grafico: questo è solo un suggerimento, l'utente può
 * cambiarlo.
 */

export type ChartType = 'table' | 'number' | 'bar' | 'line'

type Row = Record<string, unknown>

/** Valori non-null di una colonna (per campionare il tipo). */
function nonNullValues(data: Row[], key: string): unknown[] {
  return data.map((r) => r[key]).filter((v) => v !== null && v !== undefined)
}

/**
 * Colonna numerica DA GRAFICARE: tutti i non-null sono numeri finiti.
 * Esclude le colonne identificative (nome 'id' o che termina con '_id'): sono
 * numeri ma non hanno senso su un asse Y. Fonte unica di verità (usata anche da
 * ResultChart).
 */
export function isNumericColumn(data: Row[], key: string): boolean {
  const name = key.toLowerCase()
  if (name === 'id' || name.endsWith('_id')) return false
  const vals = nonNullValues(data, key)
  return vals.length > 0 && vals.every((v) => typeof v === 'number' && Number.isFinite(v))
}

/** Colonna data/mese: per nome (data|mese|month|anno|giorno|date) o valori tipo YYYY-MM(-DD). */
function isDateColumn(data: Row[], key: string): boolean {
  if (/data|mese|month|anno|giorno|date/i.test(key)) return true
  const vals = nonNullValues(data, key)
  return vals.length > 0 && vals.every((v) => typeof v === 'string' && /^\d{4}(-\d{2}){0,2}/.test(v))
}

/** Tipo di grafico suggerito dalla forma di columns+data. */
export function suggestChartType(columns: string[], data: Row[]): ChartType {
  if (data.length === 0) return 'table'

  const numeric = columns.filter((c) => isNumericColumn(data, c))
  const dateish = columns.filter((c) => isDateColumn(data, c) && !numeric.includes(c))
  const text = columns.filter((c) => !numeric.includes(c) && !dateish.includes(c))

  // 1 sola cifra
  if (columns.length === 1 && numeric.length === 1 && data.length === 1) return 'number'

  // serie temporale
  if (numeric.length === 1 && dateish.length === 1) return 'line'

  // categorie
  if (numeric.length === 1 && text.length === 1 && dateish.length === 0) return 'bar'

  return 'table'
}

/**
 * Tipi di grafico SENSATI per la forma dei dati (per abilitare/disabilitare i
 * pulsanti). Stessi criteri di suggestChartType. 'table' è sempre inclusa.
 * Il tipo suggerito viene sempre aggiunto in coda (coerenza: mai suggerire un
 * tipo che risulterebbe disabilitato).
 */
export function enabledChartTypes(columns: string[], data: Row[]): ChartType[] {
  const types = new Set<ChartType>(['table']) // fallback universale

  const numeric = columns.filter((c) => isNumericColumn(data, c))
  const dateish = columns.filter((c) => isDateColumn(data, c) && !numeric.includes(c))
  const text = columns.filter((c) => !numeric.includes(c) && !dateish.includes(c))

  // numero: 1 riga con esattamente 1 colonna numerica
  if (data.length === 1 && numeric.length === 1) types.add('number')

  // barre: categoria (non numerica) + 1 numerica, almeno 2 righe
  if (numeric.length === 1 && text.length + dateish.length >= 1 && data.length >= 2) types.add('bar')

  // linea: categoria data/mese + 1 numerica, almeno 2 righe
  if (numeric.length === 1 && dateish.length === 1 && data.length >= 2) types.add('line')

  // coerenza: il tipo suggerito è sempre tra quelli abilitati
  types.add(suggestChartType(columns, data))

  return Array.from(types)
}
